/**
 * Structured client logger.
 *
 * - All output gated behind __DEV__ (no production console noise).
 * - Tagged by module for easy filtering.
 * - Drop-in replacement for console.log('[TAG]', ...) scattered across the codebase.
 *
 * Usage:
 *   import { createLogger } from '@/lib/logger';
 *   const log = createLogger('AUTH');
 *   log.info('Session established');        // [AUTH] Session established
 *   log.warn('Token refresh slow', { ms }); // [AUTH] ⚠ Token refresh slow { ms: 4200 }
 *   log.error('Sign-out failed', err);      // [AUTH] ✖ Sign-out failed Error: ...
 */

export interface Logger {
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
}

/**
 * Create a tagged logger. In production builds all info/warn calls are no-ops.
 * Errors always log (they indicate real problems worth capturing in crash reporters).
 */
export function createLogger(tag: string): Logger {
  const prefix = `[${tag}]`;

  return {
    info: (...args: unknown[]) => {
      if (__DEV__) console.log(prefix, ...args);
    },
    warn: (...args: unknown[]) => {
      if (__DEV__) console.warn(prefix, '⚠', ...args);
    },
    error: (...args: unknown[]) => {
      // Errors always log — useful for crash reporters even in production.
      console.error(prefix, '✖', ...args);
    },
  };
}
